import React, { useRef, useState, useEffect } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { NumberInput } from "react-admin";
import InputAdornment from "@material-ui/core/InputAdornment";
import classnames from "classnames";
import Button from "@material-ui/core/Button";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { useForm } from "react-final-form";

const useStyles = makeStyles((theme) => ({
    root: {},
    muiInput: {
        paddingRight: 0,
    },
    input: {
        "&::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
        "&::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
    },
    arrows: {
        position: "absolute",
        width: 30,
        flexShrink: 0,
        right: 0,
        top: 0,
        bottom: 0,
        maxHeight: "none",
        height: "auto",
        background: theme.palette.secondary.main,
    },
    arrow: {
        background: theme.palette.common.black,
        color: theme.palette.common.white,
        padding: theme.spacing(0.5),
        position: "absolute",
        width: "100%",
        height: "50%",
        left: 0,
        right: 0,
        minWidth: 0,
        "& > span": {
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
        },
    },
    arrowUp: {
        top: 0,
        bottom: "50%",
    },
    arrowDown: {
        top: "50%",
        bottom: 0,
    },
}));

const CCTNumberInputArrows = ({
    classes = {},
    position = "end",
    inputRef,
    form,
    source,
    step,
    max,
    min,
    formValue,
    ...props
}) => {
    const handleClick = (val) => {
        if (!(inputRef && inputRef.current)) {
            return;
        }
        const currentValue = parseFloat(inputRef.current.value || 0);

        if (val > 0 && max !== undefined && currentValue >= max) {
            return;
        }
        if (val < 0 && min !== undefined && currentValue <= min) {
            return;
        }

        val > 0 ? inputRef.current.stepUp() : inputRef.current.stepDown();
        form && source && form.change(source, inputRef.current.value);
    };

    const currentValue = formValue !== undefined ? parseFloat(formValue) : 0;
    const isUpDisabled = max !== undefined && currentValue >= max;
    const isDownDisabled = min !== undefined && currentValue <= min;

    return (
        <InputAdornment className={classes.arrows} position={position}>
            <Button
                onClick={() => handleClick(1)}
                className={classnames(classes.arrow, classes.arrowUp)}
                variant="outlined"
                disabled={isUpDisabled}
            >
                <KeyboardArrowUpIcon />
            </Button>
            <Button
                onClick={() => handleClick(-1)}
                className={classnames(classes.arrow, classes.arrowDown)}
                variant="outlined"
                disabled={isDownDisabled}
            >
                <KeyboardArrowDownIcon />
            </Button>
        </InputAdornment>
    );
};

const CCTNumberInput = ({
    children,
    className,
    source,
    inputProps: _inputProps,
    step = 1,
    ...props
}) => {
    const form = useForm();
    const classes = useStyles();
    const inputRef = useRef();
    const [formValue, setFormValue] = useState(null);

    useEffect(() => {
        if (!form || !source) return;

        const subscription = form.subscribe(
            (state) => {
                const fieldValue = state.values[source];
                setFormValue(fieldValue);
                if (inputRef.current) {
                    inputRef.current.value = fieldValue || 0;
                }
            },
            { values: true }
        );

        return subscription;
    }, [form, source]);

    const inputProps = {
        ..._inputProps,
        className: classes.input,
        ref: inputRef,
    };

    // eslint-disable-next-line react/jsx-no-duplicate-props
    return (
        <NumberInput
            step={step}
            InputProps={{
                className: classes.muiInput,
                endAdornment: (
                    <CCTNumberInputArrows
                        classes={classes}
                        inputRef={inputRef}
                        form={form}
                        source={source}
                        step={step}
                        max={_inputProps?.max}
                        min={_inputProps?.min}
                        formValue={formValue}
                    />
                ),
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={inputProps}
            className={classnames(classes.root, className)}
            source={source}
            {...props}
        />
    );
};

export default CCTNumberInput;
